import ContactsIcon from "@mui/icons-material/ContactsRounded";
import { OrgsSyncPage } from "apps/web/src/pages/Auth/Staff/OrgManagement/OrgsSyncPage";
import { SportsPage } from "apps/web/src/pages/Auth/Staff/OrgManagement/SportsPage";
import { Route, Routes } from "react-router-dom-v5-compat";
import { useEnvironmentType } from "../../../../contexts/useEnvType";
import { useUser } from "../../../../utils/tokens";
import { AxleDataUpsertPage } from "../../../../views/Staff/AxleDataUpsert";
import { NutritionHubTasksBackfillPage } from "../../../../views/Staff/NutritionHubTasksBackfill";
import { SuperuserRoute } from "../../ProtectedRoutes";
import { SecondaryNavLayout, SecondaryNavLayoutProps } from "../../SecondaryNav/SecondaryNavLayout";
import { AxleOrgsSyncPage } from "./AxleOrgsSyncPage";
import { OrgGroupsPage } from "./OrgGroupsPage";
import { OrgsPage } from "./OrgsPage";
import { TerritoryPage } from "./TerritoryPage";
import { UserSearchPage } from "./UserSearchPage";

// full page path
export const NAV_STAFF_ORG_MANAGEMENT = "/staff/org-management";

// local child paths
export const NAV_ORG_MANAGEMENT_ORGS = "orgs";
export const NAV_ORG_MANAGEMENT_TERRITORIES = "territories";
export const NAV_ORG_MANAGEMENT_ORG_GROUPS = "org-groups";
export const NAV_ORG_MANAGEMENT_ORGS_SYNC = "orgs-sync";
export const NAV_ORG_MANAGEMENT_USER_SEARCH = "user-search";
export const NAV_ORG_MANAGEMENT_SPORTS = "sports";
export const NAV_ORG_MANAGEMENT_AXLE_ORGS_SYNC = "axle-orgs-sync";
export const NAV_ORG_MANAGEMENT_AXLE_DATA_UPSERT = "axle-data-upsert";
export const NAV_ORG_MANAGEMENT_HUB_TASKS_BACKFILL = "hub-tasks-backfill";

// full child paths
export const NAV_STAFF_ORG_MANAGEMENT_ORGS = `${NAV_STAFF_ORG_MANAGEMENT}/${NAV_ORG_MANAGEMENT_ORGS}`;

export const orgManagementLink = {
  name: "Org Management",
  icon: <ContactsIcon />,
  to: NAV_STAFF_ORG_MANAGEMENT,
};

export const orgsLink = {
  name: "Organizations",
  to: NAV_STAFF_ORG_MANAGEMENT_ORGS,
};

const { isCommercial, isH2F } = useEnvironmentType();

const getSecondaryNavLayoutProps = (withTUM: boolean, withShowAxleUpsertTab: boolean): SecondaryNavLayoutProps => ({
  current: orgManagementLink,
  enableAthleteSearch: true,
  tabs: [
    {
      name: "Organizations",
      to: NAV_ORG_MANAGEMENT_ORGS,
      requiresSuperuser: true,
    },
    {
      name: "Territories",
      to: NAV_ORG_MANAGEMENT_TERRITORIES,
      requiresSuperuser: true,
    },
    ...(isCommercial
      ? [{
        name: "Organizations Sync",
        to: NAV_ORG_MANAGEMENT_ORGS_SYNC,
        requiresSuperuser: true,
      }]
      : []),
    {
      name: "Organization Groups",
      to: NAV_ORG_MANAGEMENT_ORG_GROUPS,
      requiresSuperuser: true,
    },
    {
      name: "Users",
      to: NAV_ORG_MANAGEMENT_USER_SEARCH,
      requiresSuperuser: true,
    },
    {
      name: "Sports",
      to: NAV_ORG_MANAGEMENT_SPORTS,
    },
    ...(withShowAxleUpsertTab
      ? [
        {
          name: "Axle Data Upsert",
          to: NAV_ORG_MANAGEMENT_AXLE_DATA_UPSERT,
          requiresSuperuser: true,
        },
      ]
      : []),
    ...(withTUM
      ? [
        {
          name: "Axle Organizations Sync",
          to: NAV_ORG_MANAGEMENT_AXLE_ORGS_SYNC,
          requiresSuperuser: true,
        },
      ]
      : []),
    ...(isH2F
      ? [
        {
          name: "Hub Tasks Backfill",
          to: NAV_ORG_MANAGEMENT_HUB_TASKS_BACKFILL,
          requiresSuperuser: true,
        },
      ]
      : []),
  ],
});

type OrgManagementRouterProps = {
  withTUM: boolean;
  withShowAxleUpsertTab: boolean;
};

export const OrgManagementRouter = ({ withTUM, withShowAxleUpsertTab }: OrgManagementRouterProps) => (
  <Routes>
    <Route element={<SecondaryNavLayout {...getSecondaryNavLayoutProps(withTUM, withShowAxleUpsertTab)} />}>
      <Route
        index
        element={
          !!useUser()?.isSuperuser ? (
            <SuperuserRoute>
              <OrgsPage />
            </SuperuserRoute>
          ) : (
            <SportsPage />
          )
        }
      />
      <Route
        path={NAV_ORG_MANAGEMENT_TERRITORIES}
        element={
          <SuperuserRoute>
            <TerritoryPage />
          </SuperuserRoute>
        }
      />
      <Route
        path={NAV_ORG_MANAGEMENT_ORGS}
        element={
          <SuperuserRoute>
            <OrgsPage />
          </SuperuserRoute>
        }
      />
      {isCommercial && (
        <Route
          path={NAV_ORG_MANAGEMENT_ORGS_SYNC}
          element={
            <SuperuserRoute>
              <OrgsSyncPage />
            </SuperuserRoute>
          }
        />
      )}
      <Route
        path={NAV_ORG_MANAGEMENT_ORG_GROUPS}
        element={
          <SuperuserRoute>
            <OrgGroupsPage />
          </SuperuserRoute>
        }
      />
      <Route
        path={NAV_ORG_MANAGEMENT_USER_SEARCH}
        element={
          <SuperuserRoute>
            <UserSearchPage />
          </SuperuserRoute>
        }
      />
      <Route path={NAV_ORG_MANAGEMENT_SPORTS} element={<SportsPage />} />
      {withShowAxleUpsertTab && (
        <Route
          path={NAV_ORG_MANAGEMENT_AXLE_DATA_UPSERT}
          element={
            <SuperuserRoute>
              <AxleDataUpsertPage />
            </SuperuserRoute>
          }
        />
      )}
      {withTUM && (
        <Route
          path={NAV_ORG_MANAGEMENT_AXLE_ORGS_SYNC}
          element={
            <SuperuserRoute>
              <AxleOrgsSyncPage />
            </SuperuserRoute>
          }
        />
      )}
      {isH2F && (
        <Route
          path={NAV_ORG_MANAGEMENT_HUB_TASKS_BACKFILL}
          element={
            <SuperuserRoute>
              <NutritionHubTasksBackfillPage />
            </SuperuserRoute>
          }
        />
      )}
    </Route>
  </Routes>
);
